import { TextField } from '@rmwc/textfield';
import { IconPropT } from '@rmwc/types';
import React, { KeyboardEvent, useState } from 'react';
import { SyntheticEvent } from 'react';
import '@rmwc/textfield/styles';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Mode } from '../models/commonEnums';
import { Store } from '../reducers';
import styles from './Input.module.scss';
import MaterialIcon from './MaterialIcon';

type StateProps = {
    mode: Mode;
};

type ParentProps = {
    label: string;
    placeholder?: string;
    required?: boolean;
    helpText?: string;
    alwaysShowHelpText?: boolean;
    autoComplete?: string;
    value: string;
    trailingIcon?: string;
    leadingIcon?: string;
    invalid?: boolean;
    onChange: (value: string, id: string) => void;
    id: string;
    type?: string;
    maxLength?: number;
};

type Props = ParentProps & StateProps;
const InputElement = ({
    label,
    placeholder,
    required,
    helpText,
    autoComplete,
    value,
    trailingIcon,
    alwaysShowHelpText,
    invalid,
    onChange,
    id,
    type,
    maxLength,
    leadingIcon,
    mode,
}: Props): JSX.Element => {
    const { t: txt } = useTranslation();
    const [showPassword, setShowPassword] = useState(false);

    const changeInput = (e: SyntheticEvent<HTMLInputElement>): void => {
        const { id } = e.currentTarget;
        const newText = e.currentTarget.value;
        if (value.length === 0 && newText.length > 2) {
            const element = document.getElementById(id);
            element?.focus();
        }
        onChange(newText, id);
    };
    const getIcon = (iconName: string, trailing?: boolean): JSX.Element => (
        <MaterialIcon name={iconName} extraClass={trailing ? styles.trailingIcon : styles.icon} />
    );

    const clickShowPassword = (): void => {
        setShowPassword(!showPassword);
    };

    const getTrailingIcon = (): IconPropT | undefined => {
        if (trailingIcon) return getIcon(trailingIcon);
        else if (type === 'password') {
            return {
                icon: getIcon(showPassword ? 'visibility' : 'visibility_off', true),
                onClick: clickShowPassword,
                onKeyUp: (e: KeyboardEvent<HTMLButtonElement>): void => {
                    if (e.key === 'Enter') clickShowPassword();
                },
            };
        }
        return undefined;
    };

    const helpTextComponent = helpText && {
        persistent: !!alwaysShowHelpText,
        validationMsg: true,
        children: <div className={styles.helpText}>{txt(helpText)}</div>,
    };

    let elementStyle = styles.element;
    if (mode === Mode.b2c_mobile) elementStyle = styles.b2cMobileInput;

    return (
        <TextField
            id={id}
            type={showPassword ? 'text' : type}
            outlined
            label={txt(label)}
            required={required}
            placeholder={placeholder}
            helpText={helpTextComponent}
            autoComplete={autoComplete}
            value={value}
            trailingIcon={getTrailingIcon()}
            icon={leadingIcon && getIcon(leadingIcon)}
            invalid={invalid || undefined}
            onChange={changeInput}
            className={elementStyle}
            maxLength={maxLength}
        />
    );
};

const mapStateToProps = (state: Store): StateProps => {
    const {
        user: { mode },
    } = state;
    return {
        mode,
    };
};

export default connect(mapStateToProps)(InputElement);
